<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col cols="12">
          <h1>6 - 12 Month Plate Report</h1>
      </b-col>
    </b-row>
    <b-row>
      <b-col sm="12">
        <b-card>
          <div slot="header">
              <strong>Report Options</strong>
          </div>
          <div class="form-container">
            <b-row>
                <b-col sm="4">
                    <b-form-group>
                        <label for="company">Company</label>
                        <b-form-select id="company" v-if="companiesLoaded"  v-model="reportParams.companyId"
                            :plain="true"
                            :options="companies"
                            :disabled="!isMinimumSuperAdmin">
                        </b-form-select>
                        <div v-if="!companiesLoaded" >
                            <i class="fa fa-spin fa-spinner"></i>
                        </div>
                    </b-form-group>
                </b-col>
                <b-col sm="4">
                    <b-form-group>
                        <label for="area">Area</label>
                        <b-form-select id="area" v-if="areasLoaded"  v-model="reportParams.areaId"
                            :plain="true"
                            :options="areas"
                            :disabled="!isMinimumCompanyUser">
                        </b-form-select>
                        <div v-if="!areasLoaded" >
                          <i class="fa fa-spin fa-spinner"></i> Waiting for company selection
                        </div>
                    </b-form-group>
                </b-col>
                <b-col sm="4">
                    <b-form-group>
                        <label for="site">Site</label>
                        <b-form-select id="site" v-if="sitesLoaded"  v-model="reportParams.siteId"
                            :plain="true"
                            :options="sites"
                            :disabled="!isMinimumAreaUser">
                        </b-form-select>
                        <div v-if="!sitesLoaded" >
                          <i class="fa fa-spin fa-spinner"></i> Waiting for area selection
                        </div>
                    </b-form-group>
                </b-col>
            </b-row>
            <b-row>
              <b-col sm="2">
                <b-form-group>
                  <label for="reportType">Report Type</label>
                  <b-form-select id="reportType"  v-model="reportParams.reportType"
                      :plain="true"
                      :options="reportTypes">
                  </b-form-select>
                </b-form-group>
              </b-col>
              <b-col sm="2">
                <b-form-group>
                  <label for="reportCountryType">Country</label>
                  <b-form-select id="reportCountryType"  v-model="reportParams.reportCountryType"
                      :plain="true"
                      :options="reportCountryTypes">
                  </b-form-select>
                </b-form-group>
              </b-col>
              <b-col sm="2">
                  <b-form-group>
                      <label for="registration">Registration</label>
                      <b-form-input type="text" id="registration" class="uppercase" v-model="reportParams.regNumber"></b-form-input>
                  </b-form-group>
              </b-col>
              <b-col sm="3">
                  <b-form-group>
                      <label for="dateFrom">Date From</label>
                      <b-form-input type="date" id="dateFrom" v-model="reportParams.dateFrom"></b-form-input>
                  </b-form-group>
              </b-col>
              <b-col sm="3">
                  <b-form-group>
                      <label for="dateTo">Date To</label>
                      <b-form-input type="date" id="dateTo" v-model="reportParams.dateTo"></b-form-input>
                  </b-form-group>
              </b-col>
            </b-row>
            <b-alert class="mt-2" v-if="invalidDateRange" variant="danger" show>
              Selected dates must not be more than 1 year apart.
            </b-alert>
            <b-alert class="mt-2" v-if="dateInFutureError" variant="danger" show>
              The 'Date From' must be before or the same day as the 'Date To'. Please change the dates to continue.
            </b-alert>
            <hr />
            <b-row>
              <b-col cols="12" sm="6" md="6">
                <p>
                  <b-button v-on:click="runReport" variant="primary" :disabled="invalidDateRange || dateInFutureError">
                    <i class="fa fa-search"></i>&nbsp; Run Report
                  </b-button>
                </p>
              </b-col>
              <b-col cols="12" sm="6" md="6" class="col-right">
                <b-button v-on:click="gotoUserExports" variant="outline-download">
                  Past Reports
                </b-button>
              </b-col>
            </b-row>
          </div>
        </b-card>
      </b-col>
    </b-row>
    <b-row>
      <b-col sm="12">
        <b-card v-if="reportFetchStarted">
          <div slot="header">
              <strong>Report Results</strong>
          </div>
          <div>
            <div class="form-container">
              <b-row>
                <b-col>
                  <div v-if="reportBackgroundStatus == 'success'" class="background-report-output">
                    <i class="fa fa-check p-3 fa-4x mr-2 text-success"></i>
                    <div class="h5 text-success mb-0">Report is currently being processed</div>
                  </div>
                  <div class="text-success text-center">
                    Please be aware that this could take upto 1 hour per report request. <br />
                    When the export is ready, an email will be sent to {{ userDetails.email }}. <br />
                    Alternatively, you many check the <b-link class="font-weight-bold text-success" v-bind:href="'/#/reports/user-exports'">past reports</b-link> page shortly.
                  </div>
                  <div v-if="reportBackgroundStatus == 'error'" class="background-report-output">
                    <i class="fa fa-times-circle p-3 fa-4x mr-2 text-danger"></i>
                    <div class="h5 text-danger mb-0">There was an error when trying to build the report. Please contact your administrator.</div>
                  </div>            
                </b-col>                      
              </b-row>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import LoadingSpinner from '../../components/loadingSpinner/LoadingSpinner';
import cTable from '../../components/table/Table';
import { Switch as cSwitch } from '@coreui/vue';
import moment from 'moment';

const getHTTPHeaders = () => {
  let userObj = JSON.parse(localStorage.getItem('user'));
  let headerConfig = {
    headers: {
      'Content-Type': 'application/json;charset=UTF-8',
      'Authorization': 'Bearer ' + userObj.token,
    }
  }
  return headerConfig;
}

const getCurrentUser = () => {
    return new Promise((resolve, reject) => {
        let user = JSON.parse(localStorage.getItem('user'));
        if (user != null) {
            resolve(user);
        }
        else {
            reject();
        }
        
    });
}

const getCompaniesList = (($http) => {
  return new Promise((resolve, reject) => {
    let headersObj = getHTTPHeaders();
      $http.get('getCompaniesForSelect', headersObj)
        .then((response) => {
          resolve(response.data.companies);
        })
        .catch ((err) => {
          console.error(err);
          reject(err);
        });
  });
});

const getSiteList = (($http, areaId) => {
    return new Promise((resolve, reject) => {
        let headersObj = getHTTPHeaders();
        $http.get('getSitesForSelect?area_id=' + areaId, headersObj)
            .then((response) => {
                resolve(response.data.sites);
            })
            .catch ((err) => {
                console.error(err);
                reject(err);
            });
    });
});

const getAreaList = (($http, companyId) => {
    return new Promise((resolve, reject) => {
        let headersObj = getHTTPHeaders();
         $http.get('getAreasForSelect?company_id=' + companyId, headersObj)
            .then((response) => {
                resolve(response.data.areas);
            })
            .catch ((err) => {
                console.error(err);
                reject(err);
            });
    });
});

const fetchBackgroundReportData = (($http, reportParams) => {
    return new Promise((resolve, reject) => {
        let headersObj = getHTTPHeaders();
        let reportParamsString = `report_type=${reportParams.reportType}&date_from=${reportParams.dateFrom}&date_to=${reportParams.dateTo}&company_id=${reportParams.companyId}&registration_number=${reportParams.regNumber}&country=${reportParams.reportCountryType}&job_type=${reportParams.jobType}`;
        if (reportParams.areaId > 0) {
          reportParamsString += `&area_id=${reportParams.areaId}`;
        }
        if (reportParams.siteId > 0) {
          reportParamsString += `&site_id=${reportParams.siteId}`;
        }
        $http.get('reports/stats/background/plates?' + reportParamsString, headersObj)
            .then((response) => {
                resolve(response.data);
            })
            .catch ((err) => {
                console.error(err);
                reject(err);
            });
    });
});


export default {

  name: 'dataExport',
  components: {
    LoadingSpinner: LoadingSpinner,
    cTable,
    cSwitch
  },
  data: function () {
    return {
      reportParams: {
        companyId: 0,
        areaId: 0,
        siteId: 0,
        regNumber: '',
        wipNumber: '',
        dateFrom: moment(Date.now()).format("YYYY-MM-DD"),
        dateTo: moment(Date.now()).format("YYYY-MM-DD"),
        reportType: 'both',
        reportCountryType: 'uk',
        jobType: 'export'

      },
      invalidDateRange: false,
      dateInFutureError: false,
      reportBackgroundStatus: 'processing',
      instantRegValue: '',
      reportItems: [],
      reportRowLimit: 0,
      reportRowCount: 0,
      exportReportData: [],
      companies: [],
      areas: [],
      sites: [],
      reportTypes: [
        {'text': 'Both', 'value': 'both'},
        {'text': 'Trade', 'value': 'trade'},
        {'text': 'Retail', 'value': 'retail'}
      ],
      reportCountryTypes: [
        {'text': 'UK', 'value': 'uk'},
        {'text': 'Eire', 'value': 'eire'}
      ],
      resultsLoaded: false,
      reportFetchStarted: false,
      companiesLoaded: false,
      sitesLoaded: false,
      areasLoaded: false,
      allowRunReport: false,
      userAuthLevel : '',
      userDetails: ''
    }
  },
  created () {
    this.getInitialData();
  },
  watch: {
    'reportParams.companyId': function(val, oldVal) {
      
      // get the branches for the selected company
      // clear it out first
      this.$data.areas = [];
      getAreaList(this.$http, val)
        .then((areaList) => {
          if (this.$data.userAuthLevel == 'Super Admin' || this.$data.userAuthLevel == 'Company Users') {
            this.$data.areas.push({value:0, text: 'All Areas'});
            this.$data.reportParams.areaId = 0;
            this.$data.reportParams.siteId = 0;
          }
          areaList.map((item) => {
            this.$data.areas.push(item);
          });
          this.$data.areasLoaded = true;
        })
        .catch ((err) => {
          console.error(err);
        }); 
    },
    'reportParams.areaId': function(val, oldVal) {
      // get the sites for the selected branch
      // clear it out first
      this.$data.sites = [];
      this.$data.sites.push({value:0, text: 'All Sites'});
      this.$data.reportParams.siteId = 0;

      if (parseInt(val) > 0) {
        getSiteList(this.$http, val)
          .then((sitesList) => {
            sitesList.map((item) => {
              this.$data.sites.push(item);
            });
            this.$data.sitesLoaded = true;
            if (this.$data.userAuthLevel == 'Site Users') {
              this.$data.reportParams.siteId = this.$data.userDetails.site_id;
            }
          })
          .catch ((err) => {
            console.error(err);
          });
      }
    },
    'reportParams.siteId': function(val, oldVal) {
      this.$data.reportParams.siteId = val;
    },
    'reportParams.dateFrom': function(val, oldVal) {
      // check to make sure this date is not more than 3 months previous to the 'date to'
      const dateDiff = moment(val).diff(this.$data.reportParams.dateTo, 'years', true);
      if (dateDiff < -1) {
        this.$data.invalidDateRange = true;
      }
      else {
        this.$data.invalidDateRange = false;
        if (dateDiff > 0) {
          this.$data.dateInFutureError = true;
        }
        else {
          this.$data.dateInFutureError = false;
        }
      }
    },
    'reportParams.dateTo': function(val, oldVal) {
      // check to make sure this date is not more than 3 months after to the 'date from'
      const dateDiff = moment(val).diff(this.$data.reportParams.dateFrom, 'years', true);
      if (dateDiff > 1) {
        this.$data.invalidDateRange = true;
      }
      else {
        this.$data.invalidDateRange = false;
        if (dateDiff < 0) {
          this.$data.dateInFutureError = true;
        }
        else {
          this.$data.dateInFutureError = false;
        }
      }
    },
  },
  computed: {
    isMinimumSuperAdmin() {
      let returnVal = false;
      switch (this.$data.userAuthLevel) {
        case 'Super Admin':
        case 'Read Only':
          returnVal = true;
          break;
        default:
          returnVal = false;
      }
      
      return returnVal;
    },
    isMinimumCompanyUser() {
      let returnVal = false;
      switch (this.$data.userAuthLevel) {
        case 'Super Admin':
        case 'Company Users':
        case 'Read Only':
          returnVal = true;
          break;
        default:
          returnVal = false;
      }
      return returnVal;
    },
    isMinimumAreaUser() {
      let returnVal = false;
      switch (this.$data.userAuthLevel) {
        case 'Super Admin':
        case 'Company Users':
        case 'Area Users':
        case 'Read Only':
          returnVal = true;
          break;
        default:
          returnVal = false;
      }
      return returnVal;
    },
    isMinimumSiteUser() {
      let returnVal = false;
      switch (this.$data.userAuthLevel) {
        case 'Super Admin':
        case 'Company Users':
        case 'Area Users':
        case 'Site Users':
        case 'Read Only':
          returnVal = true;
          break;
        default:
          returnVal = false;
      }
      
      return returnVal;
    }
  },
  methods: {
    setUIForCurrentUser() {
      getCurrentUser()
      .then((userDetails) => {
        this.$data.userAuthLevel = userDetails.auth;
        this.$data.reportParams.companyId = userDetails.company_id;
        this.$data.reportParams.areaId = userDetails.area_id;
        this.$data.reportParams.siteId = userDetails.site_id;
        console.log(this.$data.reportParams);
      })
    },
    getInitialData() {
      getCurrentUser()
        .then((userDetails) => {
          this.$data.userDetails = userDetails;
          this.$data.userAuthLevel = userDetails.auth;
          this.$data.reportParams.companyId = userDetails.company_id;
          this.$data.reportParams.areaId = userDetails.area_id;
          this.$data.reportParams.siteId = userDetails.site_id;
          return getCompaniesList(this.$http);
        })
        .then((results) => {
          this.$data.companies = results;
          this.$data.companiesLoaded = true;
        });
    },
    gotoUserExports() {
      this.$router.push('/reports/user-export/dataExport');
    },
    async runReport() {
      this.$data.reportFetchStarted = true;
      this.$data.reportItems=[];

      try {
        const backgroundReportProcess = await fetchBackgroundReportData(this.$http, this.$data.reportParams);
        console.log(backgroundReportProcess.success);
        if (backgroundReportProcess.success != true) {
          this.$data.reportBackgroundStatus = 'error';
        }
        else {
          this.$data.reportBackgroundStatus = 'success';
        }
      }
      catch (e) {
        this.$data.reportBackgroundStatus = 'error';
      }

    },
  }
}
</script>

<style scoped>
  .uppercase {
    text-transform: uppercase;
  }
  .btn-outline-download {
    color: #c69500!important;
    border-color: #c69500!important;
  }
  .download-link {
    color: #c69500!important;
  }
  .background-report-output {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .toggle-option-button {
    margin-top: -5px;
  }
  .col-right {
    text-align: right;
  }
</style>
